/* @flow */

// Params
export const forStartupParam = 'for_startup';
// Login

export function loginEndpoint() {
  return (apiUrl: string) => `${apiUrl}login/`;
}

export function loginWithDoubleDutchEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/doubledutch/login/`;
}

export function loginWithExternalAuthEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/sso/login/`;
}

export function logoutEndpoint() {
  return (apiUrl: string) => `${apiUrl}logout/`;
}

// Signup

export function signupActivationEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/signup/check_code/`;
}

export function signupEmailEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/signup/check_username/`;
}

export function signupCreateUserEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/signup/create_user/`;
}

// Support

export function supportRetrieveUsernameEndpoint(eventId: number | string) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/retrieve_membership/`;
}

export function supportLoginLinkEndpoint(eventId: number | string) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/send_login_link/`;
}

export function supportContactEndpoint(evendId: number | string) {
  return (apiUrl: string) =>
    `${apiUrl}events/${evendId}/send_email_to_support/`;
}

// Users

export function usersEndpoint() {
  return (apiUrl: string, eventId: number) =>
    `${apiUrl}events/${eventId}/users/`;
}

// To be consistent with statistics, we pass the eventID
// to get the stats for the user only for this event
export function userEndpoint(id: number | string, eventId: number | string) {
  if (typeof eventId === 'number') {
    return (apiUrl: string) => `${apiUrl}users/${id}/?event=${eventId}`;
  }
  return (apiUrl: string) => `${apiUrl}users/${id}/?domain=${eventId}`;
}

// Custom endpoint for delete user, because this is the only action that we can use without event ID
export function userDeleteEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/me/`;
}

export function userStatisticsEndpoint(id: number, eventId: number) {
  return (apiUrl: string) =>
    `${apiUrl}users/${id}/statistics/?event=${eventId}`;
}

export function userCanCreateTopicEndpoint(
  id: number,
  eventId: number,
  kind?: string,
) {
  return (apiUrl: string) => {
    const baseUrl = `${apiUrl}users/${id}/can_create_topic/?event=${eventId}`;
    if (kind) {
      return `${baseUrl}&kind=${kind}`;
    }
    return baseUrl;
  };
}

export function userActivityEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}users/${id}/unavailabilities/`;
}

export function userValidateEmailEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/check_email/`;
}

export function userSetPasswordEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/set_password/`;
}

export function userSendPasswordLinkEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/reset_password_init/`;
}

export function userResetPasswordEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/reset_password/`;
}

export function userChangePasswordEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}users/${id}/change_password/`;
}

export function userAvatarEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}users/${id}/avatar/`;
}

export function userLanguagesEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/languages/`;
}

export function userRecommendationUserEndpoint(userId: number | string) {
  return (apiUrl: string) => `${apiUrl}users/${userId}/user_recommendation/`;
}

export function userRecommendationTopicEndpoint(userId: number | string) {
  return (apiUrl: string) => `${apiUrl}users/${userId}/topic_recommendation/`;
}

// Memberships

export function membershipEndpoint(eventId: number | string) {
  if (typeof eventId === 'number') {
    return (apiUrl: string) => `${apiUrl}events/${eventId}/membership/`;
  }
  return (apiUrl: string) => `${apiUrl}events/-/membership/?domain=${eventId}`;
}

export function membershipCalendarSyncStatusEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}memberships/${id}/calendar_sync_status/`;
}

export function membershipGuidanceEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}memberships/${id}/guidance/`;
}

// Events

export function eventEndpoint(id: number | string) {
  if (typeof id === 'number') {
    return (apiUrl: string) => `${apiUrl}events/${id}/`;
  }
  return (apiUrl: string) => `${apiUrl}events/-/?domain=${id}`;
}

export function eventTopicInspirationsEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/topic_inspirations/`;
}

export function timeSlotsSuggestionsEndpoint(id: number) {
  return (apiUrl: string) =>
    `${apiUrl}events/${id}/braindate_timepropositions/`;
}

// Topics

export function topicsEndpoint() {
  return (apiUrl: string, eventId: number) =>
    `${apiUrl}events/${eventId}/topics/`;
}

export function topicEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}topics/${eventId}/`;
}

export function topicConversationEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}topics/${id}/conversation/`;
}

export function topicBraindateEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}topics/${id}/braindate/`;
}

export function topicAvailableFiltersEndpoint(id: number) {
  return (apiUrl: string) =>
    `${apiUrl}topics/available_filters/?event=${id}&for_marketplace=1`;
}

export function keywordsEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/keywords/?past=false`;
}

export function featuredKeywordsEndpoint(eventId: number) {
  return (apiUrl: string) =>
    `${apiUrl}events/${eventId}/keywords/?featured=1&past=false`;
}

// Braindates

export function braindatesEndpoint(paginated?: boolean) {
  if (paginated) {
    return (apiUrl: string, eventId: number) =>
      `${apiUrl}v2/braindates/?event=${eventId}&user=me`;
  }

  return (apiUrl: string, eventId: number) =>
    `${apiUrl}events/${eventId}/braindates/?user=me`;
}

export function braindateEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}braindates/${id}/`;
}

// Conversations

export function conversationsEndpoint() {
  return (apiUrl: string) => `${apiUrl}conversations/`;
}

// Filters

export function filtersEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/filters/`;
}

export function featuredFiltersEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/keywords_categorized/`;
}

// Availability

export function availabilityEndpoint(membershipId: number) {
  return (apiUrl: string) =>
    `${apiUrl}memberships/${membershipId}/availabilities/`;
}

// FAQ

export function faqEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}events/${id}/faq/`;
}

export function faqQuestionEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}faq/questions/${id}/`;
}

// Interests

export function interestsEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}events/${id}/interests/`;
}

// Braindate feedback

export function feedbackEndpoint(braindateId: number) {
  return (apiUrl: string) => `${apiUrl}braindates/${braindateId}/feedback/`;
}

export function virtualCallEndpoint(conversationId: number) {
  return (apiUrl: string) =>
    `${apiUrl}plugins/video-call/${conversationId}/join/`;
}

export function conversationLeaveEndpoint(conversationId: number) {
  return (apiUrl: string) => `${apiUrl}conversations/${conversationId}/leave/`;
}

export function locationsEndpoint(eventId: number | string) {
  if (typeof eventId === 'number') {
    return (apiUrl: string) => `${apiUrl}events/${eventId}/locations/`;
  }
  return (apiUrl: string) => `${apiUrl}events/-/locations/?domain=${eventId}`;
}

// Custom Optins

export function customOptinEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}customoptin/?event=${eventId}`;
}

// notifications
export function notificationsEndpoint() {
  return (apiUrl: string, eventId: number) =>
    `${apiUrl}events/${eventId}/notifications/`;
}

// Notifications
export function markReadAllNotificationEndpoint(eventId: number) {
  return (apiUrl: string) =>
    `${apiUrl}notifications/mark_all_read/?event=${eventId}`;
}

export function clearAllNotificationsEndpoint() {
  return (apiUrl: string, eventId: number) =>
    `${apiUrl}notifications/clear_all/?event=${eventId}`;
}

// Expression of gratitude

export function expressionOfGratitudesEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}plugins/gratitude/?event=${eventId}`;
}

export function remainigGiftEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/gratitude/remaining_gifts/`;
}

export function getUnwrapitEligibleParticipantsEndpoint() {
  return (apiUrl: string) =>
    `${apiUrl}plugins/gratitude/eligible_participants/`;
}

export function expressionOfGratitudeEndpoint(
  eventId: number,
  expressionId: number,
) {
  return (apiUrl: string) =>
    `${apiUrl}plugins/gratitude/${expressionId}/?event=${eventId}`;
}

export function sendExpressionOfGratitudeEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/gratitude/send_expression/`;
}

// Transcribe
export function getTranscribeTokenEndpoint(braindateId: number) {
  return (apiUrl: string) =>
    `${apiUrl}plugins/transcribe/${braindateId}/get_token/`;
}
